import React, { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Stack, Tab, Tabs, Typography, useTheme } from '@mui/material';

import { TabPanel } from '@/components/Tabs';
import { FieldPrefixProvider } from '@/hooks/useFieldPrefix';
import { ReadonlyProvider } from '@/hooks/useReadonly';
import { PROCESS_SETTINGS_STEP_KEYS } from '@/root/linker';
import { borders } from '@/theme/constants';
import typography from '@/theme/typography';

import AccessInvitationTab from './AccessInvitationTab';
import MessagesTab from './MessagesTab';
import TalentRegistrationTab from './TalentRegistrationTab';
import TestPropertiesTab from './TestPropertiesTab';

interface SettingsTabProps {
  toggleFullScreenMode?: (isFullScreen: boolean) => void;
  isFullScreenMode?: boolean;
  activeTab?: string;
  onTabChange?: (activeTab: string) => void;
  className?: string;
  onTalentRegistrationFormOpen?: () => void;
  isCreateMode?: boolean;
  organizationSlug?: string;
  processId?: string;
  canUpdateAccessAndInvitation?: boolean;
  canUpdateTestProperties?: boolean;
  canUpdateTalentRegistration?: boolean;
  canUpdateMessages?: boolean;
}

const SettingsTab: FC<SettingsTabProps> = ({
  activeTab,
  onTabChange,
  toggleFullScreenMode,
  isFullScreenMode,
  className,
  onTalentRegistrationFormOpen,
  isCreateMode,
  organizationSlug,
  processId,
  canUpdateAccessAndInvitation = true,
  canUpdateTestProperties = true,
  canUpdateTalentRegistration = true,
  canUpdateMessages = true
}) => {
  const { t } = useTranslation('settings');
  const { spacing } = useTheme();

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: string) => {
      onTabChange?.(newValue);
    },
    [onTabChange]
  );

  return (
    <Box className={className} sx={{ height: isFullScreenMode ? '100%' : 'auto', mt: isFullScreenMode ? 0 : -3 }}>
      <Box
        sx={{
          position: 'sticky',
          top: spacing(-3),
          pt: 3,
          zIndex: 1,
          bgcolor: 'background.default',
          display: !isFullScreenMode ? 'block' : 'none'
        }}
      >
        <Stack direction="row" pb={3} justifyContent="space-between">
          <Typography variant="body1" sx={{ fontWeight: typography.fontWeightMedium }}>
            {t('processSettings.form.settings.title')}
          </Typography>
          <Button
            sx={{
              ...typography.body3,
              fontWeight: typography.fontWeightMedium,
              py: 0.25
            }}
          >
            {t('processSettings.form.settings.resetToDefault')}
          </Button>
        </Stack>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            minHeight: spacing(5),
            mt: 1.25,
            '& .MuiTab-root': {
              textTransform: 'none',
              borderTopRightRadius: spacing(0.5),
              borderTopLeftRadius: spacing(0.5),
              padding: spacing(1, 2),
              minHeight: spacing(5),
              color: 'text.primary',
              ...typography.body3
            },
            '& .MuiTabs-flexContainer': {
              borderBottom: borders.default,
              borderColor: 'grey.400'
            },
            '& .MuiTab-root.Mui-selected': {
              bgcolor: 'primary.100',
              color: 'primary.main',
              fontWeight: typography.fontWeightMedium
            },
            '& .MuiTab-root.Mui-disabled': {
              color: 'grey.700'
            }
          }}
        >
          <Tab
            value={PROCESS_SETTINGS_STEP_KEYS.SETTINGS_ACCESS_INVITATION}
            label={t('processSettings.form.tabs.accessInvitation')}
          />
          <Tab
            value={PROCESS_SETTINGS_STEP_KEYS.SETTINGS_TEST_PROPERTIES}
            label={t('processSettings.form.tabs.testProperties')}
          />
          <Tab
            value={PROCESS_SETTINGS_STEP_KEYS.SETTINGS_TALENT_REGISTRATION}
            label={t('processSettings.form.tabs.talentRegistration')}
          />
          <Tab
            value={PROCESS_SETTINGS_STEP_KEYS.SETTINGS_MESSAGES}
            label={t('processSettings.form.tabs.messagesEditing')}
          />
        </Tabs>
      </Box>
      <Box sx={{ height: '100%' }}>
        <TabPanel currentValue={activeTab} value={PROCESS_SETTINGS_STEP_KEYS.SETTINGS_ACCESS_INVITATION}>
          <FieldPrefixProvider prefix={PROCESS_SETTINGS_STEP_KEYS.SETTINGS_ACCESS_INVITATION}>
            <ReadonlyProvider readonly={!canUpdateAccessAndInvitation}>
              <AccessInvitationTab />
            </ReadonlyProvider>
          </FieldPrefixProvider>
        </TabPanel>
        <TabPanel currentValue={activeTab} value={PROCESS_SETTINGS_STEP_KEYS.SETTINGS_TEST_PROPERTIES}>
          <FieldPrefixProvider prefix={PROCESS_SETTINGS_STEP_KEYS.SETTINGS_TEST_PROPERTIES}>
            <ReadonlyProvider readonly={!canUpdateTestProperties}>
              <TestPropertiesTab />
            </ReadonlyProvider>
          </FieldPrefixProvider>
        </TabPanel>
        <TabPanel currentValue={activeTab} value={PROCESS_SETTINGS_STEP_KEYS.SETTINGS_TALENT_REGISTRATION}>
          <FieldPrefixProvider prefix={PROCESS_SETTINGS_STEP_KEYS.SETTINGS_TALENT_REGISTRATION}>
            <ReadonlyProvider readonly={!canUpdateTalentRegistration}>
              <TalentRegistrationTab
                toggleFullScreenMode={toggleFullScreenMode}
                isFullScreenMode={isFullScreenMode}
                onTalentRegistrationFormOpen={onTalentRegistrationFormOpen}
                isCreateMode={isCreateMode}
                organizationSlug={organizationSlug}
              />
            </ReadonlyProvider>
          </FieldPrefixProvider>
        </TabPanel>
        <TabPanel currentValue={activeTab} value={PROCESS_SETTINGS_STEP_KEYS.SETTINGS_MESSAGES}>
          <FieldPrefixProvider prefix={PROCESS_SETTINGS_STEP_KEYS.SETTINGS_MESSAGES}>
            <ReadonlyProvider readonly={!canUpdateMessages}>
              <MessagesTab organizationSlug={organizationSlug} processId={processId} />
            </ReadonlyProvider>
          </FieldPrefixProvider>
        </TabPanel>
      </Box>
    </Box>
  );
};

export default SettingsTab;
